import { useEffect, useState } from "react";
import { api } from "../../app/http/axiosClient";
import Incrementer from "./Incrementer";
import Modal from "../../app/universalComponents/Modal";
import { Link } from "react-router-dom";

function getCheckoutRedirectUrl(data) {
  if (!data) {
    return "";
  }

  if (typeof data.checkout_url === "string") {
    return data.checkout_url;
  }

  if (typeof data.checkout_url?.checkout_url === "string") {
    return data.checkout_url.checkout_url;
  }

  return "";
}

export default function TicketSelector({ ticketTypes = [] }) {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    function handlePageShow() {
      setSubmitting(false);
    }

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const [quantities, setQuantities] = useState(() => {
    return Object.fromEntries(ticketTypes.map((ticket) => [ticket.id, 0]));
  });

  function updateQuantity(ticketId, newQuantity) {
    setQuantities((currentQuantities) => ({
      ...currentQuantities,
      [ticketId]: newQuantity,
    }));
  }

  const selectedTickets = ticketTypes.filter((ticket) => {
    return (quantities[ticket.id] || 0) > 0;
  });

  const totalQuantity = ticketTypes.reduce((total, ticket) => {
    const quantity = quantities[ticket.id] || 0;
    return total + quantity;
  }, 0);

  const totalPrice = ticketTypes.reduce((total, ticket) => {
    const quantity = quantities[ticket.id] || 0;
    return total + quantity * ticket.price;
  }, 0);

  const totalBookingFee = ticketTypes.reduce((total, ticket) => {
    const quantity = quantities[ticket.id] || 0;
    return total + quantity * ticket.booking_fee;
  }, 0);

  const grandTotal = totalPrice + totalBookingFee;

  async function handleCheckoutSubmit(event) {
    event.preventDefault();
    setError("");

    const formData = new FormData(event.currentTarget);

    const fullName = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();

    const items = selectedTickets
      .map((ticket) => ({
        ticket_type_id: Number(ticket.id),
        quantity: Number(quantities[ticket.id] || 0),
      }))
      .filter((item) => item.quantity > 0);

    const payload = {
      fullName,
      email,
      items,
    };

    try {
      setSubmitting(true);

      const res = await api.post("public/payments/checkout", payload);
      const checkoutUrl = getCheckoutRedirectUrl(res.data);

      if (!checkoutUrl) {
        throw new Error("Missing checkout URL");
      }

      window.location.assign(checkoutUrl);
    } catch {
      setError("Something went wrong. Please try again later.");
      setSubmitting(false);
    }
  }

  if (ticketTypes.length === 0) {
    return (
      <aside className="md:sticky md:top-0 md:h-screen flex flex-col border-l bg-gray-200 px-8 py-8">
        <div className="flex min-h-0 flex-1 flex-col">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
            Tickets
          </h2>

          <p>No tickets have been released yet. Check back soon.</p>
        </div>
      </aside>
    );
  }

  return (
    <>
      <aside className="md:sticky md:top-0 md:h-screen flex flex-col border-l bg-gray-200 px-8 py-8">
        <div className="flex min-h-0 flex-1 flex-col">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
            Tickets
          </h2>

          <ul className="min-h-0 flex-1 overflow-y-auto no-scrollbar font-[Lekton] space-y-2">
            {ticketTypes.map((ticket) => (
              <li
                key={ticket.id}
                className="bg-white w-full p-4 border-x-8 border-dotted border-x-gray-200 outline outline-[3px] outline-offset-[-3px] outline-gray-200 hover:animate-[ticket-shake_0.25s_ease-in-out]"
              >
                <h3 className="text-2xl">{ticket.name}</h3>

                {ticket.description != null && (
                  <p className="text-sm">{ticket.description}</p>
                )}

                <hr className="my-2 border-black border-t-2 border-dashed" />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl text-gray-900">
                      €{Number(ticket.price).toFixed(2)}
                    </p>

                    <p className="text-sm uppercase">
                      excl. €{Number(ticket.booking_fee).toFixed(2)} booking fee
                    </p>
                  </div>

                  <div>
                    <Incrementer
                      value={quantities[ticket.id] || 0}
                      status={ticket.status}
                      min={0}
                      max={ticket.max}
                      onChange={(newQuantity) =>
                        updateQuantity(ticket.id, newQuantity)
                      }
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <button
          disabled={totalQuantity === 0}
          onClick={() => {
            setError("");
            setIsCheckoutOpen(true);
          }}
          className="mt-2 w-full rounded-sm bg-black text-white p-2 text-lg disabled:opacity-50 transition hover:opacity-75"
        >
          Checkout
        </button>
      </aside>

      <Modal isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)}>
        <form onSubmit={handleCheckoutSubmit} className="space-y-4 p-8">
          <article>
            <h1 className="text-xs text-left font-semibold uppercase tracking-widest text-gray-500">
              Order Summary
            </h1>

            <div className="mt-1 text-sm rounded-sm border border-gray-300 bg-white/70 p-4 shadow-sm">
              <div className="flex justify-between">
                <span>Tickets</span>
                <span>€{Number(totalPrice).toFixed(2)}</span>
              </div>

              <div className="flex justify-between">
                <span>Booking fee</span>
                <span>€{Number(totalBookingFee).toFixed(2)}</span>
              </div>

              <hr className="my-2" />

              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span>€{Number(grandTotal).toFixed(2)}</span>
              </div>
            </div>
          </article>

          <article className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="text-xs text-center lg:text-left font-semibold uppercase tracking-widest text-gray-500"
              >
                Full name
              </label>

              <input
                id="name"
                name="name"
                type="text"
                minLength={2}
                maxLength={100}
                required
                className="w-full rounded-sm border border-gray-300 p-2.5 border-b-2"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="text-xs text-center lg:text-left font-semibold uppercase tracking-widest text-gray-500"
              >
                Email address
              </label>

              <input
                id="email"
                name="email"
                type="email"
                required
                className="w-full rounded-sm border border-gray-300 p-2.5 border-b-2"
                placeholder="Enter your email"
              />
            </div>

            <label className="flex items-start gap-3 text-sm">
              <input name="terms" type="checkbox" required className="mt-1" />
              <span>
                I confirm that I have read and agree to the organiser's and platform's{" "}
                <Link
                  to="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline"
                >
                  Terms and Conditions
                </Link>
                .
              </span>
            </label>
          </article>

          {error ? (
            <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() => setIsCheckoutOpen(false)}
              className="w-full rounded-sm border border-gray-300 p-2 text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-sm bg-black p-2 text-sm text-white hover:opacity-75 disabled:opacity-50"
            >
              {submitting ? "Redirecting..." : "Proceed to Payment"}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
